import { useMutation } from '@apollo/client/react'
import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { LightModeModalContent } from '../../components/Modal/LightModeModalContent'
import { showToast } from '../../components/Toast/showToast'
import { DateInput } from '../../components/inputs/DateInput'
import { TextInput } from '../../components/inputs/TextInput'
import type { CreateApiKeyInput } from '../../gql/graphql'
import {
  apiKeysQuery,
  createApiKeyMutation,
} from '../../services/apiKeyService'
import type { CreateApiKeyRequest } from '../../services/apiKeyService'

type CreateApiKeyModalFormData = CreateApiKeyRequest

export interface CreateApiKeyModalProps extends Omit<ModalProps, 'children'> {}

export const CreateApiKeyModal = ({ ...props }: CreateApiKeyModalProps) => {
  const [createdApiKey, setCreatedApiKey] = useState<string>()

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid },
    setValue,
    reset,
  } = useForm<CreateApiKeyModalFormData>({
    defaultValues: {
      name: '',
      expiresAt: undefined,
    },
  })

  const apiKeysListVariables = {
    first: 200,
    skip: 0,
    orderBy: [{ createdDate: 'DESC' }] as const,
  }

  const [createKeyMutation, { loading: createKeyLoading }] = useMutation(
    createApiKeyMutation,
    {
      refetchQueries: [
        { query: apiKeysQuery, variables: apiKeysListVariables },
      ],
      awaitRefetchQueries: true,
      onCompleted: result => {
        const key = (result as { createApiKey?: { apiKey?: string } })
          ?.createApiKey?.apiKey
        if (key) setCreatedApiKey(key)
      },
      onError: () => {
        showToast({
          id: `api-key-create-error`,
          title: 'Error Creating API Key',
          description: `There was an error creating your API key.`,
          variant: 'error',
        })
      },
    },
  )

  const handleClose = () => {
    setCreatedApiKey(undefined)
    reset()
    props.onClose?.()
  }

  return (
    <Modal size="xl" {...props} onClose={handleClose}>
      <ModalOverlay />

      {createdApiKey ? (
        <LightModeModalContent>
          <ModalHeader>API Key Created</ModalHeader>
          <ModalCloseButton />
          <ModalBody pt="35px" pb="50px">
            <VStack gap="24px" align="start">
              <Text>Your new API key has been created.</Text>
              <Text>Please copy it now as it will not be shown again:</Text>
              <Text
                fontFamily="mono"
                p={4}
                bg="gray.100"
                borderRadius="md"
                width="100%"
              >
                {createdApiKey}
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={handleClose}>Close</Button>
          </ModalFooter>
        </LightModeModalContent>
      ) : (
        <LightModeModalContent>
          <form
            onSubmit={handleSubmit(data => {
              const input: CreateApiKeyInput = {
                name: data.name,
                expiresAt: data.expiresAt || undefined,
              }
              void createKeyMutation({ variables: { input } })
            })}
          >
            <ModalHeader>Create API Key</ModalHeader>
            <ModalCloseButton />
            <ModalBody pt="35px" pb="50px">
              <VStack gap="24px">
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <TextInput
                    {...register('name')}
                    autoComplete="off"
                    placeholder="My API Key..."
                    autoFocus={true}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Expires At (Optional)</FormLabel>
                  <DateInput
                    {...register('expiresAt')}
                    onChange={date => setValue('expiresAt', date)}
                    usePortal={false}
                  />
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                px="40px"
                isLoading={isSubmitting || createKeyLoading}
                isDisabled={!isValid}
              >
                Create
              </Button>
            </ModalFooter>
          </form>
        </LightModeModalContent>
      )}
    </Modal>
  )
}
